/**
 * Main checkout hook: composes state, effects, and sub-hooks (address, contact, order, panels, shipping, coupon).
 * Composes checkout state and helpers; return shape is {@link UseCheckoutReturn}.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { isEqual, omit } from "lodash";
import { publicViewContext } from "../context/PublicViewProvider";
import { createCloseCart } from "../actions/shop";
import { checkoutCartChecker } from "../utils/cartLocalStorage";
import type {
  CheckoutProps,
  CheckoutState,
  UseCheckoutReturn,
  ContactEntity,
} from "../components/shop/Checkout/types";
import {
  getInitialCheckoutState,
  EDIT_CHECKOUT_OMIT_KEYS,
  type PublicViewContextValue,
} from "./useCheckoutState";
import { useCheckoutShipping } from "./useCheckoutShipping";
import { useCheckoutCoupon } from "./useCheckoutCoupon";
import { useCheckoutAddress } from "./useCheckoutAddress";
import { useCheckoutContact } from "./useCheckoutContact";
import { useCheckoutOrder } from "./useCheckoutOrder";
import { useCheckoutPanels } from "./useCheckoutPanels";

export default function useCheckout(
  props: CheckoutProps
): UseCheckoutReturn | null {
  const context = useContext(publicViewContext) as unknown as
    | PublicViewContextValue
    | undefined;
  const [state, setState] = useState<CheckoutState>(() =>
    getInitialCheckoutState(props, context ?? undefined)
  );

  const prevEditDataRef = useRef<Record<string, unknown>>({});
  const prevOrderTotalRef = useRef(props.orderTotal);
  const prevCartLengthRef = useRef(props.cart.items.length);
  const prevDiscountRef = useRef<{
    orderTotal: number;
    applied_discount: CheckoutState["applied_discount"];
  }>({
    orderTotal: state.orderTotal,
    applied_discount: state.applied_discount,
  });
  const checkCouponRef = useRef<() => void>(() => {});
  const stateRef = useRef(state);
  stateRef.current = state;

  const entityFromContext = context?.publicViewEntity?.entity as unknown as
    | Record<string, unknown>
    | undefined;
  const entityId = context?.publicViewEntity?.entityId ?? props.entityId;
  const { navigate, onEditCheckout, dispatch } = props;

  const { handleRecalculateShippingCost } = useCheckoutShipping({
    setState,
    context,
    getState: () => stateRef.current,
  });

  const { checkCoupon } = useCheckoutCoupon({
    state,
    setState,
    onCreateErrorPopup: props.onCreateErrorPopup,
    context,
  });
  checkCouponRef.current = checkCoupon;

  const onCloseCart = useCallback(() => {
    return dispatch(createCloseCart(entityId) as unknown);
  }, [dispatch, entityId]);

  const onOpenCart = useCallback(() => {
    const entityLocation = context?.publicViewEntity?.entityLocation ?? "";
    Promise.resolve(onCloseCart()).then(() => {
      if (entityLocation) navigate(String(entityLocation));
    });
  }, [context?.publicViewEntity?.entityLocation, navigate, onCloseCart]);

  useEffect(() => {
    const shop_id = props.entityId;
    const localCartData = checkoutCartChecker(shop_id);
    if (!localCartData) onOpenCart();
    handleRecalculateShippingCost();
    const checkout = props.checkout;
    if (Object.values(checkout ?? {}).length === 0) {
      const data = omit(stateRef.current, EDIT_CHECKOUT_OMIT_KEYS) as Record<
        string,
        unknown
      >;
      onEditCheckout(data as Partial<CheckoutState>);
    }
  }, [
    props.entityId,
    props.checkout,
    onEditCheckout,
    handleRecalculateShippingCost,
    onOpenCart,
  ]);

  useEffect(() => {
    setState((s) => {
      if (s.orderTotal === props.orderTotal && isEqual(s.cart, props.cart)) {
        return s;
      }
      return { ...s, cart: props.cart, orderTotal: props.orderTotal };
    });
  }, [props.cart, props.orderTotal]);

  useEffect(() => {
    handleRecalculateShippingCost();
  }, [state.orderTotal, handleRecalculateShippingCost]);

  useEffect(() => {
    const data = omit(state, EDIT_CHECKOUT_OMIT_KEYS) as Record<
      string,
      unknown
    >;
    if (!isEqual(prevEditDataRef.current, data)) {
      prevEditDataRef.current = data;
      onEditCheckout(data as Partial<CheckoutState>);
    }
  }, [state, onEditCheckout]);

  useEffect(() => {
    if (
      prevOrderTotalRef.current !== props.orderTotal ||
      prevCartLengthRef.current !== props.cart.items.length
    ) {
      prevOrderTotalRef.current = props.orderTotal;
      prevCartLengthRef.current = props.cart.items.length;
      setState((s) =>
        s.avalara_summary !== false ? { ...s, avalara_summary: false } : s
      );
    }
  }, [props.orderTotal, props.cart.items.length]);

  useEffect(() => {
    const preDiscount = prevDiscountRef.current;
    const dataDiscount = {
      orderTotal: state.orderTotal,
      applied_discount: state.applied_discount,
    };
    if (!isEqual(preDiscount, dataDiscount)) {
      prevDiscountRef.current = dataDiscount;
      checkCouponRef.current();
    }
  }, [state.orderTotal, state.applied_discount]);

  const onFocus = useCallback((focus: string) => {
    return () => setState((s) => ({ ...s, focus }));
  }, []);

  const handleChangeMetaData = useCallback(
    (label: string, value: string, metadata_id: string) => {
      setState((s) => ({
        ...s,
        order_metadata: {
          ...s.order_metadata,
          [metadata_id]: { label, value },
        },
        errors: {},
      }));
    },
    []
  );

  const { checkAddressErrors, mapSelectAddress } = useCheckoutAddress({
    state,
    entityFromContext,
    context,
  });

  const { checkContactErrors, mapSelectContact } = useCheckoutContact({
    state,
  });

  const order = useCheckoutOrder({
    state,
    setState,
    props,
    context,
    entityFromContext,
    entityId,
    checkCoupon,
  });

  const panels = useCheckoutPanels({
    state,
    setState,
    context,
    entityFromContext,
    checkAddressErrors,
    checkContactErrors,
  });

  if (!context?.publicViewEntity) {
    return null;
  }

  const entityOrder = context.publicViewEntity.entityOrder;
  const entityType = context.publicViewEntity.entityType as string | undefined;
  const entityClientId = context.publicViewEntity.entityClientId;
  const entityAggregate = context.publicViewEntity.entityAggregate;
  const entityBuyInventory = context.publicViewEntity.buyInventory;
  const contact =
    (context.publicViewEntity as { contact?: ContactEntity }).contact ??
    props.contact;
  const templateColor =
    (context as { publicViewTemplate?: { templateColor?: string } })
      .publicViewTemplate?.templateColor ?? "";
  const addresses = props.addresses;
  const contacts = props.contacts;

  const checkout: UseCheckoutReturn = {
    state,
    setState,
    entityFromContext,
    templateColor,
    entityOrder,
    entityType,
    entityClientId,
    entityAggregate: Number(entityAggregate) as number,
    entityBuyInventory,
    contact,
    addresses,
    contacts,
    checkCoupon,
    onFocus,
    checkAddressErrors,
    checkContactErrors,
    checkCompanyNameErrors: panels.checkCompanyNameErrors,
    checkPanelErrors: panels.checkPanelErrors,
    isShopAggregate: panels.isShopAggregate,
    toggleOpenPanel: panels.toggleOpenPanel,
    isPanelOpen: panels.isPanelOpen,
    showCompanyName: panels.showCompanyName,
    showMetadataList: panels.showMetadataList,
    showComments: panels.showComments,
    getShippingAddress: order.getShippingAddress,
    handleChangeMetaData,
    mapSelectContact,
    mapSelectAddress,
    calculateCheckoutAmounts: order.calculateCheckoutAmounts,
    isCheckoutTaxValid: order.isCheckoutTaxValid,
    handleSubmitOrder: order.handleSubmitOrder,
    isAvalara: order.isAvalara,
  };

  return checkout;
}
